/**
 * Clue repository — typed query layer over the sql.js database.
 *
 * The adaptive engine never holds all ~540k clues in memory. Instead, each turn
 * the session controller asks the repo for a small candidate pool (due review
 * clues by id + a random sample from the relevant tags) and the pure selection
 * policy picks from that. This keeps memory flat regardless of bank size.
 */
import type { Database } from 'sql.js';
import type { Board, Clue, Round, TagInfo } from '../engine/types';

function rowToClue(r: {
  id: number;
  category: string;
  clue: string;
  response: string;
  value: number;
  round: string;
  air_date: string;
  dd: number;
  difficulty: number;
  tags: string | null;
}): Clue {
  return {
    i: r.id,
    c: r.category,
    q: r.clue,
    a: r.response,
    v: r.value,
    r: r.round as Round,
    d: r.air_date,
    df: r.difficulty,
    t: r.tags ? r.tags.split(',').map(Number) : [],
    ...(r.dd ? { dd: 1 as const } : {}),
  };
}

const CLUE_COLS = `c.id, c.category, c.clue, c.response, c.value, c.round,
  c.air_date, c.dd, c.difficulty,
  (SELECT group_concat(tag_id) FROM clue_tag WHERE clue_id = c.id) AS tags`;

export interface CategoryInfo {
  title: string;
  count: number;
}

export interface DeckInfo {
  id: number;
  name: string;
  blurb: string;
  count: number;
}

export class ClueRepo {
  constructor(private db: Database) {}


  /** Run a query, returning rows as plain objects. */
  private query<T = Record<string, unknown>>(sql: string, params: unknown[] = []): T[] {
    const stmt = this.db.prepare(sql);
    stmt.bind(params as never);
    const out: T[] = [];
    while (stmt.step()) out.push(stmt.getAsObject() as T);
    stmt.free();
    return out;
  }

  clueCount(): number {
    const r = this.query<{ n: number }>('SELECT COUNT(*) AS n FROM clue');
    return r[0]?.n ?? 0;
  }

  getDecks(): DeckInfo[] {
    return this.query<DeckInfo>(
      'SELECT id, name, blurb, count FROM deck WHERE count > 0 ORDER BY id',
    );
  }

  /** Random sample of clues from a curated focus deck. */
  sampleByDeck(deckId: number, limit: number, exclude: number[] = []): Clue[] {
    const params: unknown[] = [deckId];
    let extra = '';
    if (exclude.length > 0) {
      extra = ` AND c.id NOT IN (${exclude.map(() => '?').join(',')})`;
      params.push(...exclude);
    }
    params.push(limit);
    return this.query(
      `SELECT ${CLUE_COLS} FROM clue c
       WHERE c.id IN (SELECT clue_id FROM focus_deck WHERE deck_id = ?)${extra}
       ORDER BY RANDOM() LIMIT ?`,
      params,
    ).map((r) => rowToClue(r as never));
  }

  getTags(): TagInfo[] {
    return this.query<{ id: number; name: string; count: number }>(
      'SELECT id, name, count FROM tag WHERE count > 0 ORDER BY name',
    ).map((r) => ({ id: r.id, name: r.name, count: r.count }));
  }

  /** Fetch specific clues by id (used for due review cards). */
  getClues(ids: number[]): Clue[] {
    if (ids.length === 0) return [];
    const ph = ids.map(() => '?').join(',');
    return this.query(`SELECT ${CLUE_COLS} FROM clue c WHERE c.id IN (${ph})`, ids).map(
      (r) => rowToClue(r as never),
    );
  }

  /**
   * Random sample of clues carrying ANY of `tagIds` (or all clues if empty),
   * excluding `exclude`. Drives both tag-drill and adaptive candidate pools.
   */
  sampleByTags(tagIds: number[], limit: number, exclude: number[] = []): Clue[] {
    const params: unknown[] = [];
    let where = '';
    if (tagIds.length > 0) {
      const ph = tagIds.map(() => '?').join(',');
      where = `WHERE c.id IN (SELECT clue_id FROM clue_tag WHERE tag_id IN (${ph}))`;
      params.push(...tagIds);
    }
    if (exclude.length > 0) {
      const ph = exclude.map(() => '?').join(',');
      where += `${where ? ' AND' : 'WHERE'} c.id NOT IN (${ph})`;
      params.push(...exclude);
    }
    params.push(limit);
    return this.query(
      `SELECT ${CLUE_COLS} FROM clue c ${where} ORDER BY RANDOM() LIMIT ?`,
      params,
    ).map((r) => rowToClue(r as never));
  }

  /** Random sample of clues from one exact original category title. */
  sampleByCategory(title: string, limit: number, exclude: number[] = []): Clue[] {
    const params: unknown[] = [title];
    let where = 'WHERE c.category = ?';
    if (exclude.length > 0) {
      where += ` AND c.id NOT IN (${exclude.map(() => '?').join(',')})`;
      params.push(...exclude);
    }
    params.push(limit);
    return this.query(
      `SELECT ${CLUE_COLS} FROM clue c ${where} ORDER BY RANDOM() LIMIT ?`,
      params,
    ).map((r) => rowToClue(r as never));
  }

  /**
   * Original category titles for the drill-by-category picker. With `q`, matches
   * titles containing it; otherwise returns the most frequently aired ones.
   * Only categories with >=5 clues (a full board's worth) are offered.
   */
  searchCategories(q: string, limit = 60): CategoryInfo[] {
    const params: unknown[] = [];
    let where = '';
    if (q.trim()) {
      where = 'WHERE category LIKE ?';
      params.push(`%${q.trim()}%`);
    }
    params.push(limit);
    return this.query<{ title: string; count: number }>(
      `SELECT category AS title, COUNT(*) AS count FROM clue ${where}
       GROUP BY category HAVING count >= 5 ORDER BY count DESC, category LIMIT ?`,
      params,
    );
  }

  /**
   * Build a board from 6 distinct categories — each one's 5 clues that aired
   * together in a single episode — drawn from anywhere in the archive. All 6
   * come from the same round (J or DJ) so the value ladder is consistent.
   * `board_column` (built offline) lists every (air_date, round, category) group
   * that has a full 5 clues. Returns null only if the bank lacks enough columns.
   */
  randomBoard(rng: () => number = Math.random): Board | null {
    const round: Round = rng() < 0.5 ? 'J' : 'DJ';
    const cands = this.query<{ category: string; clue_ids: string }>(
      `SELECT category, clue_ids FROM board_column WHERE round = ? ORDER BY RANDOM() LIMIT 80`,
      [round],
    );

    const categories: BoardCategoryT[] = [];
    const seenNames = new Set<string>();
    for (const cand of cands) {
      if (categories.length === 6) break;
      if (seenNames.has(cand.category)) continue;
      const ids = cand.clue_ids.split(',').map(Number);
      const clues = this.getClues(ids).sort((a, b) => a.v - b.v);
      if (clues.length !== 5) continue; // defensive
      categories.push({ name: cand.category, clues });
      seenNames.add(cand.category);
    }
    if (categories.length < 6) return null;
    return { date: 'various episodes', round, categories };
  }

  /** Lazily index every 5-clue column, keyed by date+round+category. */
  private columns: Map<string, number[]> | null = null;
  private columnList: { name: string; ids: number[] }[] = [];
  // Tab is a safe delimiter: the source is tab-separated, so no field has a tab.
  private colKey(d: string, r: string, c: string): string {
    return [d, r, c].join('\t');
  }
  private ensureColumns(): void {
    if (this.columns) return;
    this.columns = new Map();
    const rows = this.query<{ air_date: string; round: string; category: string; clue_ids: string }>(
      `SELECT air_date, round, category, clue_ids FROM board_column`,
    );
    for (const r of rows) {
      const ids = r.clue_ids.split(',').map(Number);
      this.columns.set(this.colKey(r.air_date, r.round, r.category), ids);
      this.columnList.push({ name: r.category, ids });
    }
  }

  /**
   * Pick one full category (its 5 aired clues) for adaptive practice, biased
   * toward the user's weak tags: sample weak-tagged clues and serve the category
   * one of them belongs to. Falls back to a random category. `exclude` holds
   * category names already played this session.
   */
  randomAdaptiveCategory(
    weakTagIds: number[],
    rng: () => number = Math.random,
    exclude: Set<string> = new Set(),
  ): BoardCategoryT | null {
    this.ensureColumns();
    const build = (name: string, ids: number[]): BoardCategoryT => ({
      name,
      clues: this.getClues(ids).sort((a, b) => a.v - b.v),
    });

    // Weak-tag-biased candidates: each clue points at the category it aired in.
    for (const c of this.sampleByTags(weakTagIds, 50)) {
      if ((c.r !== 'J' && c.r !== 'DJ') || exclude.has(c.c)) continue;
      const ids = this.columns!.get(this.colKey(c.d, c.r, c.c));
      if (ids && ids.length === 5) return build(c.c, ids);
    }

    // Fallback: a random full column not already played.
    const list = this.columnList;
    for (let i = 0; i < 80 && list.length > 0; i++) {
      const col = list[Math.floor(rng() * list.length)]!;
      if (exclude.has(col.name)) continue;
      if (col.ids.length === 5) return build(col.name, col.ids);
    }
    return null;
  }
}

type BoardCategoryT = Board['categories'][number];
